import { useCallback, useMemo, useRef, useSyncExternalStore, type Dispatch, type SetStateAction } from "react";
import { useLocation } from "wouter";
import { listNavigationState } from "@/lib/list-navigation-state";

/**
 * useState for list filters/search/sort. A stable, explicit key identifies the
 * control; the route isolates detail pages. An optional context can isolate
 * explicit search links without making item-selection query params reset lists.
 * Updates are saved synchronously, including when navigation immediately follows.
 * Supports functional setters, arrays and Sets without JSON serialization.
 */
export function useListState<T>(
  key: string,
  initialState: T | (() => T),
  context = "",
): [T, Dispatch<SetStateAction<T>>] {
  const [pathname] = useLocation();
  const scopedKey = `${pathname}::${key}::${context}`;
  const initial = useRef(initialState);
  initial.current = initialState;
  const fallback = useMemo(() => {
    const value = initial.current;
    return typeof value === "function" ? (value as () => T)() : value;
  }, [scopedKey]);
  const getSnapshot = useCallback(
    () => listNavigationState.has(scopedKey) ? listNavigationState.get<T>(scopedKey) : fallback,
    [scopedKey, fallback],
  );
  const value = useSyncExternalStore(listNavigationState.subscribe, getSnapshot, getSnapshot);
  const setValue = useCallback<Dispatch<SetStateAction<T>>>((next) => {
    const value = typeof next === "function"
      ? (next as (previous: T) => T)(getSnapshot())
      : next;
    listNavigationState.set(scopedKey, value);
  }, [scopedKey, getSnapshot]);
  return [value, setValue];
}
